type SearchRequest = {
  indexName?: string;
  params?: Record<string, unknown>;
};

/**
 * One of the hierarchical menu's count queries, which the helper sends for
 * each category level above the refined one: no hits, only `categories.lvl*`
 * facets. The category nav's sibling and ancestor counts come from these.
 */
export const isCategoryCountRequest = (params?: Record<string, unknown>) => {
  const facets = params?.facets;
  const facetList = Array.isArray(facets) ? facets : [facets];
  return (
    params?.hitsPerPage === 0 &&
    facetList.every(
      (facet) => typeof facet === 'string' && facet.startsWith('categories.lvl')
    )
  );
};

/**
 * The category a count query counts within: the parent level the helper
 * refines it to. The root level query has none.
 */
const getCountCategory = (params?: Record<string, unknown>) => {
  const facetFilters = params?.facetFilters;
  const categoryFilter = (Array.isArray(facetFilters) ? facetFilters : [])
    .flat()
    .find(
      (filter): filter is string =>
        typeof filter === 'string' && filter.startsWith('categories.lvl')
    );
  return categoryFilter?.slice(categoryFilter.indexOf(':') + 1);
};

/**
 * Sends each category count query with `filtersFor(category)` in place of the
 * page's filters. A category page pins every query to its own
 * `categoryPageId`, so a sibling's count would otherwise only be the products
 * it shares with the current category. `null` leaves every request as is.
 */
export const withCategoryCountFilters = <T extends SearchRequest>(
  requests: T[],
  filtersFor: ((category: string) => string) | null
): T[] =>
  filtersFor === null
    ? requests
    : requests.map((request) => {
        const category =
          isCategoryCountRequest(request.params) &&
          getCountCategory(request.params);
        return category
          ? {
              ...request,
              params: { ...request.params, filters: filtersFor(category) },
            }
          : request;
      });
